// frontend/src/components/WarehousesList.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { 
  Edit, Trash2, Plus, Building, User, Warehouse, 
  Filter, Search, ChevronDown, AlertCircle,
  Loader2, Eye, Globe, MapPin
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./WareHousesList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const WarehousesList = () => {
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();
  
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companies, setCompanies] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // For system admins
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');

  useEffect(() => {
    fetchWarehouses();
    
    // System admins can filter by company
    if (isSystemAdmin) {
      fetchCompanies();
    }
  }, [selectedCompany]);

  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      
      let endpoint = `${url}/api/warehouse`;
      const params = {};
      
      // System admins can filter by company
      if (isSystemAdmin && selectedCompany) {
        params.company_id = selectedCompany;
      }
      
      const response = await axios.get(endpoint, { params });
      
      if (response.data.success) {
        setWarehouses(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch warehouses");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch warehouses");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`);
      
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${url}/api/warehouse/${id}`);
      
      if (response.data.success) {
        toast.success(`"${name}" deleted successfully`);
        fetchWarehouses();
      } else {
        toast.error(response.data.message || "Failed to delete warehouse");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete warehouse");
    }
  };

  const handleViewWarehouse = (warehouse) => {
    // Show warehouse details in a modal or alert
    alert(`Warehouse Details:\n\nCode: ${warehouse.warehouse_code}\nName: ${warehouse.warehouse_name}\nLocation: ${warehouse.location || "N/A"}\nCompany: ${warehouse.company_id?.company_name}\nCreated By: ${warehouse.created_by?.name || "System"}\nCreated: ${new Date(warehouse.created_at).toLocaleDateString()}\nStatus: ${warehouse.status}`);
  };

  const handleEditWarehouse = (id) => {
    navigate(`/warehouse/edit/${id}`);
  };

  // Filter warehouses based on search term
  const filteredWarehouses = warehouses.filter(warehouse =>
    warehouse.warehouse_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    warehouse.warehouse_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    warehouse.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    warehouse.company_id?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    warehouse.created_by?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading warehouses...</p>
      </div>
    );
  }

  return (
    <div className="warehouses-container">
      {/* Header */}
      <div className="header">
        <div>
          <h2>Warehouses</h2>
          <p className="company-info">
            {isSystemAdmin ? (
              <>
                <Globe size={14} /> All Companies
              </>
            ) : (
              <>
                <Building size={14} /> {company?.company_name} ({company?.company_code})
              </>
            )}
          </p>
        </div>
        
        <div className="header-actions">
          {/* Filter Toggle for System Admins */}
          {isSystemAdmin && (
            <button 
              className="filter-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={16} />
              Filter
            </button>
          )}
          
          <button 
            onClick={() => navigate("/AddWareHouses")} 
            className="add-button"
          >
            <Plus size={16} /> Add Warehouse
          </button>
        </div>
      </div>

      {/* Filters for System Admins */}
      {isSystemAdmin && showFilters && (
        <div className="filters-container">
          <div className="filter-group">
            <label>Filter by Company:</label>
            <div className="select-wrapper">
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={loadingCompanies}
              >
                <option value="">All Companies</option>
                {companies.map((comp) => (
                  <option key={comp._id} value={comp._id}>
                    {comp.company_name} ({comp.company_code})
                  </option>
                ))}
              </select>
              <Building size={16} className="select-icon" />
            </div>
          </div>
          
          <button 
            className="clear-filters"
            onClick={() => {
              setSelectedCompany("");
              setShowFilters(false);
            }}
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search warehouses by code, name, location, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="stats">
          <span>Showing: {filteredWarehouses.length} of {warehouses.length} warehouses</span>
          {selectedCompany && (
            <span className="filter-badge">
              <Building size={12} />
              {companies.find(c => c._id === selectedCompany)?.company_name || "Filtered"}
            </span>
          )}
        </div>
      </div>

      {/* Warehouses Table */}
      <div className="table-container">
        <table className="styled-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Warehouse Code</th>
              <th>Warehouse Name</th>
              <th>Location</th>
              {isSystemAdmin && <th>Company</th>}
              <th>Created By</th>
              <th>Created Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredWarehouses.length === 0 ? (
              <tr>
                <td colSpan={isSystemAdmin ? 9 : 8} className="no-data">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Warehouse size={48} color="#ccc" />
                    <p style={{ margin: '15px 0', color: '#666' }}>
                      {searchTerm || selectedCompany
                        ? "No warehouses found matching your criteria"
                        : "No warehouses found"
                      }
                    </p>
                    <button 
                      className="add-button" 
                      onClick={() => navigate("/AddWareHouses")}
                    >
                      <Plus size={16} /> Create First Warehouse
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredWarehouses.map((warehouse, index) => (
                <tr key={warehouse._id}>
                  <td>{index + 1}</td>
                  <td>
                    <div className="warehouse-code">
                      <Warehouse size={14} className="mr-2" color="#667eea" />
                      <strong>{warehouse.warehouse_code}</strong>
                    </div>
                  </td>
                  
                  <td>
                    <div className="warehouse-name">
                      <strong>{warehouse.warehouse_name}</strong>
                    </div>
                  </td>
                  
                  <td>
                    <div className="location-cell">
                      {warehouse.location ? (
                        <>
                          <MapPin size={12} className="mr-2" />
                          <span>{warehouse.location}</span>
                        </>
                      ) : (
                        <span className="text-muted">Not specified</span>
                      )}
                    </div>
                  </td>
                  
                  {isSystemAdmin && (
                    <td>
                      <div className="company-cell">
                        <Building size={12} className="mr-2" />
                        <div>
                          <div>{warehouse.company_id?.company_name || "N/A"}</div>
                          <small>{warehouse.company_id?.company_code || ""}</small>
                        </div>
                      </div>
                    </td>
                  )}
                  
                  <td>
                    <div className="user-cell">
                      <User size={12} className="mr-2" />
                      <div>
                        <div>{warehouse.created_by?.name || "System"}</div>
                        <small>{warehouse.created_by?.email || warehouse.created_by?.Usercomp_email || ""}</small>
                      </div>
                    </div>
                  </td>
                  
                  <td>{new Date(warehouse.created_at).toLocaleDateString()}</td>
                  
                  <td>
                    <span className={`status-badge ${warehouse.status}`}>
                      {warehouse.status}
                    </span>
                  </td>
                  
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view-btn"
                        onClick={() => handleViewWarehouse(warehouse)}
                        title="View Details"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEditWarehouse(warehouse._id)}
                        title="Edit Warehouse"
                        disabled={warehouse.status === "inactive"}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(warehouse._id, warehouse.warehouse_name)}
                        title="Delete Warehouse"
                        disabled={warehouse.status === "inactive"}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WarehousesList;